/**
 * Push Notification Service
 * Verwaltet Web-Push-Subscriptions für iOS und Android.
 */

import apiService from './apiService';

const PUSH_PREF_KEY = 'push_permission_asked';

/**
 * Prüft ob Push-Benachrichtigungen grundsätzlich unterstützt werden.
 */
export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Gibt den aktuellen Benachrichtigungs-Berechtigungsstatus zurück.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Prüft ob der Browser schon gefragt wurde (damit wir nicht beim nächsten Login erneut fragen).
 */
export function wasPermissionAsked() {
  return localStorage.getItem(PUSH_PREF_KEY) === 'true';
}

export function markPermissionAsked() {
  localStorage.setItem(PUSH_PREF_KEY, 'true');
}

/**
 * Konvertiert einen Base64-String in einen Uint8Array (für VAPID publicKey).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/**
 * Fordert die Benachrichtigungsberechtigung an und abonniert Push.
 * @returns {Promise<{success: boolean, reason?: string}>}
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { success: false, reason: 'not_supported' };
  }

  // 1. VAPID Public Key vom Backend holen
  let vapidPublicKey;
  try {
    const data = await apiService.get('/push/vapid-public-key');
    vapidPublicKey = data.publicKey;
  } catch {
    return { success: false, reason: 'vapid_unavailable' };
  }

  // 2. Benachrichtigungsberechtigung anfordern
  const permission = await Notification.requestPermission();
  markPermissionAsked();

  if (permission !== 'granted') {
    return { success: false, reason: 'permission_denied' };
  }

  // 3. Service Worker holen
  const registration = await navigator.serviceWorker.ready;

  // 4. Push-Subscription erstellen
  let subscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  } catch (err) {
    console.error('[Push] Subscription fehlgeschlagen:', err);
    return { success: false, reason: 'subscription_failed' };
  }

  // 5. Subscription an Backend senden
  try {
    const subscriptionJson = subscription.toJSON();
    await apiService.post('/push/subscribe', {
      endpoint: subscriptionJson.endpoint,
      keys: subscriptionJson.keys
    });
    return { success: true };
  } catch (err) {
    console.error('[Push] Subscription speichern fehlgeschlagen:', err);
    return { success: false, reason: 'save_failed' };
  }
}

/**
 * Kündigt die Push-Subscription.
 * @returns {Promise<{success: boolean}>}
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { success: false };

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const p256dh = subscription.toJSON().keys?.p256dh;
      await subscription.unsubscribe();
      await apiService.delete('/push/unsubscribe', { p256dh });
    }
    return { success: true };
  } catch (err) {
    console.error('[Push] Unsubscribe fehlgeschlagen:', err);
    return { success: false };
  }
}

/**
 * Gibt den aktuellen Push-Status zurück (ob dieser Browser abonniert ist).
 * @returns {Promise<boolean>}
 */
export async function getLocalPushSubscriptionStatus() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Gibt detaillierte Push-Status-Informationen zurück.
 * @returns {Promise<{
 *   enabled: boolean,
 *   subscriptionCount: number,
 *   lastUpdated: string|null,
 *   swVersion: string,
 *   systemVersion: string,
 *   vapidConfigured: boolean,
 *   stale: boolean,
 *   checkedAt: string,
 *   supported: boolean,
 *   permission: string,
 *   swActive: boolean
 * }>}
 */
export async function getPushStatus() {
  const status = {
    supported: isPushSupported(),
    permission: getNotificationPermission(),
    swActive: false,
    swVersion: 'unbekannt',
    enabled: false,
    subscriptionCount: 0,
    lastUpdated: null,
    systemVersion: '1440',
    vapidConfigured: false,
    stale: false,
    checkedAt: new Date().toISOString()
  };

  if (!status.supported) {
    return status;
  }

  try {
    // Prüfe Service Worker
    const registration = await navigator.serviceWorker.ready;
    status.swActive = true;

    const subscription = await registration.pushManager.getSubscription();
    status.enabled = !!subscription;

    // Versuche vom Server detaillierte Infos zu holen
    try {
      const serverStatus = await apiService.get('/push/status?swVersion=v3');
      return {
        ...status,
        ...serverStatus,
        swActive: true,
        swVersion: 'v3'
      };
    } catch (apiError) {
      console.warn('Could not fetch push status from server:', apiError);
      // Fallback: Verwende nur lokale Infos
      return status;
    }
  } catch (err) {
    console.warn('Error getting push status:', err);
    return status;
  }
}

/**
 * Sendet eine Test-Benachrichtigung (nur Admin).
 */
export async function sendTestPush() {
  return apiService.postWithLongTimeout('/push/test', {}, 60000);
}

/**
 * Prüft ob die gespeicherte Subscription noch mit dem Server übereinstimmt und erneuert sie
 * wenn nötig. Sollte beim App-Start aufgerufen werden.
 */
export async function refreshPushSubscriptionIfNeeded() {
  if (!isPushSupported()) return;
  if (Notification.permission !== 'granted') return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Subscription existiert nicht mehr (z.B. nach Browser-Update oder iOS-Reset)
      // Automatisch neu abonnieren
      console.log('[Push] Subscription fehlt – re-subscribing...');
      await subscribeToPush();
      return;
    }

    // Prüfen ob Subscription beim Server noch bekannt ist
    try {
      const status = await apiService.get('/push/status');
      if (!status.enabled) {
        // Server kennt diese Subscription nicht mehr → neu registrieren
        console.log('[Push] Subscription auf Server nicht gefunden – re-registering...');
        const sub = subscription.toJSON();
        await apiService.post('/push/subscribe', {
          endpoint: sub.endpoint,
          keys: sub.keys
        });
      }
    } catch {
      // Server nicht erreichbar – kein Problem, beim nächsten Start erneut prüfen
    }
  } catch (err) {
    console.warn('[Push] refreshPushSubscriptionIfNeeded error:', err);
  }
}
